import { useState, useEffect, useCallback } from 'react';
import { X, RotateCcw, Save, Sparkles, Target, BookOpen, AlertTriangle } from 'lucide-react';

const TABS = [
  { key: 'general', label: 'General', icon: Sparkles },
  { key: 'objetivos', label: 'Objetivos', icon: Target },
  { key: 'bitacora', label: 'Bitácora', icon: BookOpen },
];

export function PromptsModal({ open, onClose, getPrompts, savePrompt, restorePrompt }) {
  const [activeTab, setActiveTab] = useState('general');
  const [prompts, setPrompts] = useState({});
  const [drafts, setDrafts] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);

  const showToast = useCallback((msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  }, []);

  // Load prompts on open
  useEffect(() => {
    if (!open) return;
    setLoading(true);
    getPrompts()
      .then((data) => {
        setPrompts(data);
        const d = {};
        for (const key of Object.keys(data)) {
          d[key] = data[key].prompt_text;
        }
        setDrafts(d);
      })
      .catch(() => showToast('Error al cargar prompts.', 'error'))
      .finally(() => setLoading(false));
  }, [open, getPrompts, showToast]);

  if (!open) return null;

  const current = prompts[activeTab];
  const draftText = drafts[activeTab] || '';
  const isDefault = current?.is_default;
  const hasChanges = current && draftText !== current.prompt_text;
  const charCount = draftText.length;

  const handleSave = async () => {
    if (!draftText.trim()) {
      showToast('El prompt no puede estar vacío.', 'error');
      return;
    }
    setSaving(true);
    try {
      await savePrompt(activeTab, draftText);
      // Reload
      const data = await getPrompts();
      setPrompts(data);
      const d = {};
      for (const key of Object.keys(data)) d[key] = data[key].prompt_text;
      setDrafts(d);
      showToast('Prompt guardado correctamente.');
    } catch {
      showToast('Error al guardar.', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleRestore = async () => {
    if (!window.confirm('¿Restaurar este prompt al valor original por defecto?')) return;
    setSaving(true);
    try {
      await restorePrompt(activeTab);
      const data = await getPrompts();
      setPrompts(data);
      const d = {};
      for (const key of Object.keys(data)) d[key] = data[key].prompt_text;
      setDrafts(d);
      showToast('Prompt restaurado al valor por defecto.');
    } catch {
      showToast('Error al restaurar.', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    const anyUnsaved = Object.keys(prompts).some(
      (k) => drafts[k] !== prompts[k]?.prompt_text
    );
    if (anyUnsaved && !window.confirm('Tienes cambios sin guardar. ¿Cerrar de todos modos?')) return;
    onClose();
  };

  return (
    <div className="prompts-overlay" data-testid="prompts-modal-overlay" onClick={handleClose}>
      <div className="prompts-modal" data-testid="prompts-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="prompts-header">
          <h2 className="prompts-title">Configurar Prompts IA</h2>
          <button className="prompts-close-btn" onClick={handleClose} data-testid="prompts-close-btn">
            <X className="h-4 w-4" />
          </button>
        </div>

        <p className="prompts-note">Estos prompts controlan directamente lo que la IA devuelve en cada resumen.</p>

        {/* Tabs */}
        <div className="prompts-tabs" data-testid="prompts-tabs">
          {TABS.map(({ key, label, icon: Icon }) => (
            <button
              key={key}
              className={`prompts-tab ${activeTab === key ? 'active' : ''}`}
              onClick={() => setActiveTab(key)}
              data-testid={`prompts-tab-${key}`}
            >
              <Icon className="h-3.5 w-3.5" />
              {label}
            </button>
          ))}
        </div>

        {/* Content */}
        {loading ? (
          <div className="prompts-loading">Cargando prompts...</div>
        ) : (
          <div className="prompts-body">
            <div className="prompts-meta">
              <span className="prompts-char-count">{charCount} caracteres</span>
              {!isDefault && (
                <span className="prompts-custom-badge">Personalizado</span>
              )}
              {hasChanges && (
                <span className="prompts-unsaved-badge">
                  <AlertTriangle className="h-3 w-3" />
                  Sin guardar
                </span>
              )}
            </div>

            <textarea
              className="prompts-textarea"
              value={draftText}
              onChange={(e) => setDrafts((prev) => ({ ...prev, [activeTab]: e.target.value }))}
              rows={14}
              placeholder="Escribe el prompt del sistema..."
              data-testid="prompts-textarea"
            />

            {/* Actions */}
            <div className="prompts-actions">
              <button
                className="prompts-action-btn prompts-restore-btn"
                onClick={handleRestore}
                disabled={saving || isDefault}
                title={isDefault ? 'Ya está en el valor por defecto' : 'Restaurar prompt original'}
                data-testid="prompts-restore-btn"
              >
                <RotateCcw className="h-3.5 w-3.5" />
                Restaurar defecto
              </button>
              <button
                className="prompts-action-btn prompts-save-btn"
                onClick={handleSave}
                disabled={saving || !hasChanges || !draftText.trim()}
                data-testid="prompts-save-btn"
              >
                <Save className="h-3.5 w-3.5" />
                {saving ? 'Guardando...' : 'Guardar'}
              </button>
            </div>
          </div>
        )}

        {/* Toast */}
        {toast && (
          <div className={`prompts-toast ${toast.type === 'error' ? 'error' : ''}`} data-testid="prompts-toast">
            {toast.msg}
          </div>
        )}
      </div>
    </div>
  );
}
